import ArrowRight from 'components/ReusableComponents/Icons/ArrowRight'
import CopyIcon from 'components/ReusableComponents/Icons/CopyIcon'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import { Cell, Row } from 'components/ReusableComponents/Table'
import useBrand from 'hooks/useBrand'
import useCopyText from 'hooks/useCopyText'
import HotelIcon from 'img/hotels.svg'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { useToasts } from 'react-toast-notifications'
import userApi from 'services/api/user'

import { selectCurrentHotelName } from '../../../accessors'
import {
  generateShortNameAndBrand,
  postAndSelectSubsidiary,
  savePrevPath
} from '../../../actions'
import { saveSubsidiary } from '../../../utils/helpers'
import styles from './HotelCard.module.scss'

/**
 *
 * @typedef Hotel
 * @property {string} hotelName
 * @property {string} bookingEngine
 * @property {string} chainCode
 * @property {string} parentChainCode
 * @property {string} textMarketingUrlKey
 * @property {string} addedOn
 * @property {number} addedBy
 * @property {string} updatedOn
 * @property {number} updatedBy
 * @property {string} id
 * @property {number} numberOfRooms
 * @property {string} pms
 * @property {boolean} isMissingSomeInformation
 * @property {number} displayId
 * @property {boolean} isDeleted
 * @property {number} status
 * @property {object[]} subsidiaryPmsUsers
 * @property {number} subsidiaryPmsUsers.id
 * @property {string} subsidiaryPmsUsers.subsidiaryId
 * @property {string} subsidiaryPmsUsers.email
 * @property {string} subsidiaryPmsUsers.firstName
 * @property {string} subsidiaryPmsUsers.lastName
 * @property {string} subsidiaryPmsUsers.birthDate
 * @property {string} subsidiaryPmsUsers.addedOn
 * @property {number} subsidiaryPmsUsers.addedBy
 * @property {string} merchantName
 * @property {boolean} inviteBackIsDisabled
 * @property {object[]} inviteBlockOn
 * @property {string} inviteBlockOn.addedBy
 * @property {string} inviteBlockOn.addedOn
 * @property {string} inviteBlockOn.reason
 * @property {boolean} pmsSyncIsConnected
 * @property {boolean} isServiceStarted
 * @property {boolean} isCampaignsEnabled
 * @property {boolean} isTcrActivated
 * @property {boolean} isPhoneEnabled
 * @property {object[]} csmTags
 * @property {number} csmTags.id
 * @property {string} csmTags.name
 * @property {string} csmTags.addedBy
 * @property {string} csmTags.addedOn
 * @property {object[]} tags
 * @property {number} tags.id
 * @property {string} tags.name
 * @property {string} tags.addedBy
 * @property {string} tags.addedOn
 * @property {string} processKind
 *
 * @typedef CSM
 * @property {string} id
 * @property {string} fullName
 * @property {string[]} assignedSubsidiaryIds
 *
 * @typedef {object} Props
 * @property {Hotel} hotel
 * @property {CSM[]} csmList
 * @property {() => void} getCustomerSuccessManagers
 */

/**
 *
 * @type {React.FC<Props>}
 * @returns {React.ReactElement}
 */
export const HotelCard = props => {
  const { hotel, csmList, getCustomerSuccessManagers } = props
  const dispatch = useDispatch()
  const history = useHistory()
  const { addToast } = useToasts()
  const copy = useCopyText()
  const currentHotelName = useSelector(selectCurrentHotelName)
  const { logoUrl, colors } = useBrand({
    subsidiaryShortLinkToken: hotel.textMarketingUrlKey,
    parentChainCode: hotel.parentChainCode
  })

  const currentCsmId = csmList.find(csm =>
    csm.assignedSubsidiaryIds.includes(hotel.id)
  )?.id
  const normalizedCurrentCsmId = currentCsmId != null ? String(currentCsmId) : 'unassigned'

  const [csmId, setCsmId] = React.useState(normalizedCurrentCsmId)
  const [isPopupVisible, setIsPopupVisible] = React.useState(false)
  const [isTagPopupVisible, setIsTagPopupVisible] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)

  React.useEffect(() => {
    if (!isLoading) {
      setCsmId(normalizedCurrentCsmId)
    }
  }, [normalizedCurrentCsmId, isLoading])

  const handleMouseEnter = () => {
    setIsPopupVisible(true)
  }

  const handleMouseLeave = () => {
    setIsPopupVisible(false)
  }

  const handleMouseEnterTag = () => {
    setIsTagPopupVisible(true)
  }

  const handleMouseLeaveTag = () => {
    setIsTagPopupVisible(false)
  }

  function handleHotelClick() {
    if (currentHotelName !== hotel.hotelName) {
      dispatch(savePrevPath('/hotelDetails'))
      saveSubsidiary({
        subsidiaryId: hotel.id
      })
      dispatch(postAndSelectSubsidiary(hotel.id))
    } else {
      history.push('/hotelDetails')
    }
  }

  async function handleCsmChange(id) {
    const normalizedId = id != null ? String(id) : 'unassigned'
    if (normalizedId === csmId) {
      return
    }

    setIsLoading(true)
    addToast('Saving...', { appearance: 'info' })

    try {
      if (csmId !== 'unassigned' && normalizedId === 'unassigned') {
        await userApi.deleteUserRole({
          dashboardUserId: csmId,
          subsidiaryId: hotel.id
        })
        setCsmId('unassigned')
      } else {
        await userApi.updateUserRole({
          dashboardUserId: normalizedId,
          subsidiaryId: hotel.id,
          role: 'Csm'
        })
        setCsmId(normalizedId)
      }
      addToast('Saved', { appearance: 'success' })
    } catch (error) {
      addToast('Error while saving', { appearance: 'error' })
    } finally {
      getCustomerSuccessManagers()
      setIsLoading(false)
    }
  }

  const pmsUser = hotel.subsidiaryPmsUsers?.[0]
  const { brand, shortName } = generateShortNameAndBrand(hotel.hotelName)

  const tags = hotel.csmTags.slice(0, 3).map(tag => tag.name)
  const overFlowTags = hotel.csmTags?.slice(3).map(tag => tag.name)

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {isPopupVisible && (
        <div className={styles.popup}>
          <div className={styles.logoWrapper} style={{ backgroundColor: colors.primary }}>
            <img
              className={styles.logo}
              src={logoUrl}
              onError={e => {
                e.currentTarget.onerror = null
                e.currentTarget.src = HotelIcon
              }}
              alt="hotel logo"
            />
          </div>

          <div className={styles.infoCol}>
            <div className={styles.info}>
              <div>
                <span className={styles.name}>BE</span>
                <span className={styles.value}>{hotel.bookingEngine ?? 'N/A'}</span>
              </div>
              <div>
                <span className={styles.name}>PMS</span>
                <span className={styles.value}>{hotel.pms ?? 'N/A'}</span>
              </div>
            </div>

            <div className={styles.details}>
              {hotel.textMarketingUrlKey && (
                <div onClick={() => copy(hotel.textMarketingUrlKey)}>
                  <span className={styles.value}>{hotel.textMarketingUrlKey}</span>
                  <CopyIcon width={12} />
                </div>
              )}
              {pmsUser?.email && (
                <div onClick={() => copy(pmsUser.email)}>
                  <span className={styles.value}>{pmsUser.email}</span>
                  <CopyIcon width={12} />
                </div>
              )}
              {pmsUser?.subsidiaryId && (
                <div onClick={() => copy(pmsUser.subsidiaryId)}>
                  <span className={styles.value}>{pmsUser.subsidiaryId}</span>
                  <CopyIcon width={12} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <Row className={styles.row}>
        <Cell>
          <div className={styles.hotelName}>
            <span className={styles.brandName}>{brand}</span>
            <span className={styles.name}>
              <span className={styles.id}>#{hotel.displayId} </span>
              {shortName}
            </span>
          </div>
        </Cell>

        <Cell>
          <div className={styles.groupName}>
            <span className={styles.merchantName}>{hotel.merchantName ?? '-'}</span>
          </div>
        </Cell>

        <Cell data-tip={'TEST'} data-for="hotels-by-csm-tooltip">
          <div className={styles.tags}>
            {hotel.csmTags.length === 0 && <span className={styles.noTag}>-</span>}
            {tags.map((tag, index) => (
              <span key={index} className={styles.tag}>
                {tag}
              </span>
            ))}
            {hotel.csmTags.length > 3 && (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <p
                className={styles.moreText}
                onMouseEnter={handleMouseEnterTag}
                onMouseLeave={handleMouseLeaveTag}
              >
                +{hotel.csmTags.length - 3}
                {isTagPopupVisible && (
                  <div className={`${styles.popup} ${styles.tagPopup}`}>
                    <div className={styles.infoCol}>
                      <div className={styles.info}>
                        <div className={styles.tags}>
                          {overFlowTags.map((tag, index) => (
                            <span key={index} className={styles.tag}>
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </p>
            )}
          </div>
        </Cell>

        <Cell>
          <div className={styles.customerSuccessManager}>
            <Dropdown
              className={styles.csmDropdown}
              value={csmId}
              disabled={isLoading}
              options={csmList.reduce(
                (prev, curr) => ({
                  ...prev,
                  [String(curr.id)]: curr.fullName
                }),
                {
                  unassigned: 'Unassigned'
                }
              )}
              onChange={e => handleCsmChange(e.target.value)}
            />
          </div>
        </Cell>
        <Cell>
          <div className={styles.ctaCol}>
            <button
              disabled={hotel.isDeleted}
              className={styles.cta}
              onClick={handleHotelClick}
            >
              <ArrowRight />
            </button>
          </div>
        </Cell>
      </Row>
    </div>
  )
}
